import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";

import { joinTerms, materialTerms } from "../pages/materialTerms";
import type { MaterialTerm } from "../pages/materialTerms";
import type { RecipeDetail } from "../api/types";
import { Empty } from "./Empty";

/** How long the tick stays before the clipboard comes back. Long enough to be
 *  seen after the eye has left the button, short enough that a second copy is
 *  not waiting on it. */
const TICK_MS = 1400;

/** Square corners, like everything else the client draws: a clipboard whose
 *  corners were rounded would be the only rounded thing on the page. */
function ClipboardIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M5.5 2.5H3.5V14.5H12.5V2.5H10.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path d="M5.5 1.5H10.5V3.5H5.5Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function TickIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 8.2 6.4 11.6 13 5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

/** The list of names to hand the market analyzer.
 *
 *  The analyzer is a separate desktop GUI with an "Only items" field, and that
 *  field wants a comma-separated list of broker search terms. Everything the
 *  reader needs is already on this page — it is just spread down a tree and
 *  spelled one name per row — so this dialog is a transcription, not a new
 *  decision: every material, checked, in one string, with the boxes there to
 *  take out the few he does not want to spend the scan on.
 *
 *  A native <dialog>. Esc, the focus trap, inertness of the page behind and the
 *  ::backdrop pseudo-element are the browser's job and it does them better than
 *  a div would; what is left to do by hand is the backdrop click, which the
 *  element deliberately does not implement. */
export function ScanListDialog({
  open,
  onClose,
  detail,
}: {
  open: boolean;
  onClose: () => void;
  detail: RecipeDetail;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const titleId = useId();
  const fieldId = useId();

  const terms = useMemo(() => materialTerms(detail), [detail]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [copied, setCopied] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const tick = useRef<number | undefined>(undefined);

  // Everything starts checked, and a reopen starts over. A dialog that
  // remembered last time's unchecks would be a setting nobody knew they had
  // set — the reader opens this to scan a recipe, not to resume an edit.
  //
  // Seeded during render rather than in an effect, the way Recipe.tsx seeds its
  // open rows: an effect would paint one frame of empty boxes first, and on a
  // list of forty rows that frame is visible.
  const [seeded, setSeeded] = useState<MaterialTerm[] | null>(null);
  const wanted = open ? terms : null;
  if (seeded !== wanted) {
    setSeeded(wanted);
    setSelected(
      wanted === null
        ? new Set()
        : new Set(wanted.filter((term) => !term.duplicate).map((term) => term.key)),
    );
    // The two things the copy button says are about the last press, not about
    // this recipe. The tick expires on its own, but the blocked sentence does
    // not — left alone it would greet the next opening with a complaint about
    // a copy nobody here has attempted.
    setCopied(false);
    setBlocked(false);
  }

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    // showModal() on an already-open dialog throws, and so does close() on a
    // shut one under some engines. The element's own state is the truth here,
    // because Esc closes it without asking this component first.
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => () => window.clearTimeout(tick.current), []);

  const selectable = terms.filter((term) => !term.duplicate);
  const text = joinTerms(
    selectable.filter((term) => selected.has(term.key)).map((term) => term.name),
  );

  function toggle(key: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  async function copy() {
    setBlocked(false);
    // The DOM types say navigator.clipboard is always there. It is not: it is
    // absent on an insecure origin, which is how this app is reached over a LAN
    // by IP, and the write can be refused outright by permissions policy.
    const clipboard = navigator.clipboard as Clipboard | undefined;
    try {
      if (!clipboard) throw new Error("no clipboard");
      await clipboard.writeText(text);
      setCopied(true);
      window.clearTimeout(tick.current);
      tick.current = window.setTimeout(() => setCopied(false), TICK_MS);
    } catch {
      // Not an error band: nothing has gone wrong with the data, and the reader
      // can still have the string. Hand him the selection and say so.
      setCopied(false);
      setBlocked(true);
      fieldRef.current?.focus();
      fieldRef.current?.select();
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="scan-dialog"
      aria-labelledby={titleId}
      // Esc closes the element without passing through this component, so the
      // element's own close event is what keeps the page's state in step.
      onClose={onClose}
      // The backdrop is painted by the dialog itself, so a click that lands on
      // the element rather than on one of its children landed outside the box.
      // The dialog carries no padding of its own, which is what makes that
      // test exact.
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose();
      }}
    >
      <div className="scan-head">
        <h2 id={titleId}>Scan list</h2>
        <span className="note">paste into the market analyzer's Only items</span>
        <button type="button" className="scan-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>

      {terms.length === 0 ? (
        // Unreachable while the scroll row is always appended, and kept anyway:
        // it costs one branch, and the alternative failure is a dialog with a
        // heading, an empty box and nothing to say.
        <Empty>This recipe lists no materials.</Empty>
      ) : (
        <>
          <div className="strip">
            <div className="group">
              {/* Forty boxes unchecked one at a time is the pain this dialog
                  would otherwise have. Actions, not states, so no aria-pressed
                  and no underline. */}
              <button type="button" onClick={() => setSelected(new Set(selectable.map((t) => t.key)))}>
                all
              </button>
              <button type="button" onClick={() => setSelected(new Set())}>
                none
              </button>
            </div>
            <span className="spacer" />
            <span>
              {selected.size} of {selectable.length}
            </span>
          </div>

          <ul className="scan-list">
            {terms.map((term) => (
              <li
                key={term.key}
                className="scan-row"
                style={{ "--depth": term.depth } as CSSProperties}
              >
                {term.duplicate ? (
                  // No checkbox: the same word cannot be in the string twice,
                  // so this row is here to keep the tree's shape and to point
                  // at the row that owns it. A spacer where the box would be,
                  // the way the Materials table aligns a leaf under a parent.
                  <span className="scan-dup">
                    <span className="scan-spacer" />
                    <span>{term.name}</span>
                    <span className="dim">· listed above</span>
                  </span>
                ) : (
                  <label>
                    <input
                      type="checkbox"
                      checked={selected.has(term.key)}
                      onChange={() => toggle(term.key)}
                    />
                    <span>{term.name}</span>
                    {term.scroll ? <span className="dim">· the recipe scroll</span> : null}
                    {/* The same words the row on the page behind this one uses.
                        A scan is what fills these in, so they are checked like
                        everything else — the note says why they are worth it. */}
                    {term.unscanned ? <span className="dim">· not scanned</span> : null}
                  </label>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="scan-out">
        <label htmlFor={fieldId}>Only items</label>
        <div className="scan-out-row">
          {/* Mono, and the one place on this page that earns it: this is
              literal text the reader will paste somewhere else, which is the
              whole of what DESIGN.md reserves the command face for. */}
          <textarea id={fieldId} ref={fieldRef} readOnly rows={3} value={text} />
          <button
            type="button"
            className={copied ? "scan-copy done" : "scan-copy"}
            aria-label="Copy to clipboard"
            disabled={text === ""}
            onClick={copy}
          >
            {copied ? <TickIcon /> : <ClipboardIcon />}
          </button>
        </div>
        {/* One line, always present, so the dialog does not jump when it fills.
            role="status" because the tick is the only other confirmation and a
            glyph swap announces nothing. */}
        <p className="dim scan-said" role="status">
          {blocked
            ? "Your browser would not let the page copy. The text is selected — copy it by hand."
            : copied
              ? "Copied."
              : ""}
        </p>
      </div>
    </dialog>
  );
}
